import { useCallback, useState } from 'react';
import { getTemplateDefaultName, FileTemplateKey } from '../utils';
import { NewEntryDialogState, TreeNode, ContextMenuState } from '../types';
import { useUI } from '../contexts';

type UseNewEntryParams = {
  tree: TreeNode[];
  contextMenu: ContextMenuState;
  closeContextMenu: () => void;
  fetchTree: (projectId?: string) => Promise<void>;
};

export function useNewEntry({
  tree,
  contextMenu,
  closeContextMenu,
  fetchTree,
}: UseNewEntryParams) {
  // Get UI state from context
  const { setShowCreateMenu } = useUI();
  const [newEntryDialog, setNewEntryDialog] = useState<NewEntryDialogState>({ open: false, type: 'file', parent: null });
  const [newEntryName, setNewEntryName] = useState<string>(getTemplateDefaultName('component'));
  const [newEntryTemplate, setNewEntryTemplate] = useState<FileTemplateKey>('component');

  const handleNewEntry = useCallback(
    (type: 'file' | 'folder') => {
      if (!contextMenu.node && tree.length === 0) return closeContextMenu();
      setNewEntryDialog({ open: true, type, parent: contextMenu.node || null });
      if (type === 'file') {
        setNewEntryTemplate('component');
        setNewEntryName(getTemplateDefaultName('component'));
      } else {
        setNewEntryName('NewFolder');
      }
    },
    [closeContextMenu, contextMenu.node, tree]
  );

  const handleCreateDirectory = useCallback(() => {
    setShowCreateMenu(false);
    setNewEntryDialog({ open: true, type: 'folder', parent: null });
    setNewEntryName('NewFolder');
  }, [setShowCreateMenu]);

  const handleCreateFile = useCallback(() => {
    setShowCreateMenu(false);
    setNewEntryDialog({ open: true, type: 'file', parent: null });
    setNewEntryTemplate('component');
    setNewEntryName(getTemplateDefaultName('component'));
  }, [setShowCreateMenu]);

  const handleAddProject = useCallback(() => {
    setShowCreateMenu(false);
    fetchTree('123456');
  }, [fetchTree, setShowCreateMenu]);

  const closeNewEntryDialog = useCallback(() => {
    setNewEntryDialog({ open: false, type: 'file', parent: null });
  }, []);

  return {
    newEntryDialog,
    setNewEntryDialog,
    newEntryName,
    setNewEntryName,
    newEntryTemplate,
    setNewEntryTemplate,
    handleNewEntry,
    handleCreateDirectory,
    handleCreateFile,
    handleAddProject,
    closeNewEntryDialog,
  };
}
